import React, { useContext, useEffect, useRef, useState } from 'react';
import proptypes from 'prop-types';
const { func, number, oneOfType, shape, string } = proptypes;
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Tooltip } from '@thd-olt-component-react/tooltip';
import { Row, Col, Placeholder } from '@thd-olt-component-react/core-ui';
import { RadioButtonChecked, SpecialSavings } from '@one-thd/sui-icons';
import { Details } from './Details';
import { DetailsCard } from './DetailsCard';
import { useVisuallySimilar } from '../../hooks/useVisuallySimilar';
import {
  imageStyle,
  triggerAnalytics
} from '../../helpers';
import './styles/hotspot.scss';

const Hotspot = ({
  hsXPosition, hsYPosition, itemID, product, storeId, schemaName, productPrice, index,
  currentHotspotIndex, setCurrentHotspotIndex
}) => {
  const hotspotRef = useRef();

  const [hotspotClicked, setClickHotspot] = useState(false);
  const { channel } = useContext(ExperienceContext);

  const hasVisuallySimilar = product?.info?.hasVisuallySimilar;
  const { visSimSkus = [], loading } = useVisuallySimilar({
    itemID,
    hasVisuallySimilar
  });

  const visSimLoaded = !loading && visSimSkus;
  const emptyResponse = visSimSkus && visSimSkus.length === 0;
  const visSimReady = visSimLoaded && hasVisuallySimilar;
  const shouldPopulateSimilarItems = visSimReady && !emptyResponse;
  const shouldDisplayLoader = (hasVisuallySimilar && !visSimLoaded);

  const carouselClass = visSimSkus.length > 1 ? 'thd-react-tooltip__content-carousel' : '';

  const hotspotClick = (event) => {
    if (index === currentHotspotIndex) {
      setTimeout(() => {
        setClickHotspot(false);
        setCurrentHotspotIndex(null);
      }, 300);

    } else {
      setClickHotspot(true);
      setCurrentHotspotIndex(index);
    }

    triggerAnalytics('product-hotspots.tooltip', {
      channel,
      itemID,
      leftPosition: hsXPosition,
      product,
      topPosition: hsYPosition
    });
  };

  const removeActiveHotspotClass = (event) => {
    if (!(event?.target.nodeName === 'path' || event?.target.nodeName === 'svg')) {
      setCurrentHotspotIndex(null);
    }
  };

  useEffect(() => {
    document.body.addEventListener('click', removeActiveHotspotClass);
    return () => {
      document.body.removeEventListener('click', removeActiveHotspotClass);
    };
  }, []);

  const hotspotTagButton = () => {
    return (
      <button
        tabIndex="0"
        type="button"
        className={currentHotspotIndex === index ? 'tag tippy-active' : 'tag'}
        id="hotspot-container"
        onClick={hotspotClick}
        ref={hotspotRef}
        style={imageStyle(hsXPosition, hsYPosition)}
        aria-label="Product Hotspot"
      >
        <span
          // eslint-disable-next-line max-len
          className={classNames('sui-bg-primary sui-absolute sui-inset-0 sui-opacity-100 sui-pointer-events-none sui-rounded-full',
            {
              'sui-m-auto sui-h-3 sui-w-3': currentHotspotIndex !== index,
              'sui-h-10 sui-w-10': currentHotspotIndex === index
            })}
          style={currentHotspotIndex === index ? { margin: '-8px' } : {}}
        />
        <div
          tabIndex="-1"
          className={classNames('circle-tag-icon', {
            'sui-relative': currentHotspotIndex !== index,
            'sui-hidden': currentHotspotIndex === index,
          })}
          role="circleTagHotspot"
          aria-label="circleTagHotspot"
        >
          <RadioButtonChecked />
        </div>
        <div
          tabIndex="-1"
          className={classNames('shoppable-tag-icon', {
            'sui-relative': currentHotspotIndex === index,
            'sui-hidden': currentHotspotIndex !== index,
          })}
          role="hotspot"
          aria-label="hotspot"
        >
          <SpecialSavings />
        </div>
      </button>
    );
  };

  if (shouldDisplayLoader) {
    return (
      <div id={`tooltip-wrapper-div-${itemID}`}>
        <Tooltip
          boundary="viewport"
          className={carouselClass}
          channel="desktop"
          content={hotspotClicked && (
            <Row className="details__main-row details__main-row--loading show-loading-animation">
              <Col className="details__left-column" fallback="6" nopadding>
                <Placeholder
                  className="details__loader--left"
                  type="rect"
                  showLoadingAnimation
                />
              </Col>
              <Col className="details__right-column" fallback="6">
                <div className="details__loader--right">
                  { new Array(6).fill('').map((value, i) => (
                    <Placeholder
                      type="text"
                      className="placeholder-text-row"
                      showLoadingAnimation
                      key={i}
                    />
                  ))}
                </div>
              </Col>
            </Row>
          )}
          closeButton
          showClose={false}
          interactive
          appendTo={() => document.querySelector(`#tooltip-wrapper-div-${itemID}`)}
        >
          {hotspotTagButton()}
        </Tooltip>
      </div>
    );
  }

  const carouselSkus = visSimSkus.length > 5 ? visSimSkus.slice(0, 5) : visSimSkus;
  return (
    <div id={`tooltip-wrapper-div-${itemID}`}>
      <Tooltip
        boundary="viewport"
        className={carouselClass}
        channel="desktop"
        content={hotspotClicked && (shouldPopulateSimilarItems
          ? (
            <Details
              itemID={itemID}
              sku={product}
              storeId={storeId}
              productPrice={productPrice}
              schemaName={schemaName}
              carouselSkus={carouselSkus}
            />
          ) : (
            <DetailsCard
              product={product}
              storeId={storeId}
              index={0}
              anchorClass="details"
            />
          )
        )}
        closeButton
        showClose={false}
        interactive
        appendTo={() => document.querySelector(`#tooltip-wrapper-div-${itemID}`)}
      >
        {hotspotTagButton()}
      </Tooltip>
    </div>
  );
};

Hotspot.displayName = 'Hotspot';

Hotspot.propTypes = {
  hsXPosition: number.isRequired,
  hsYPosition: number.isRequired,
  itemID: string.isRequired,
  product: shape({
    info: shape({
      brandName: string,
      imageUrl: string,
      productLabel: string
    }),
    productUrl: string,
    ratingsReviews: shape({
      averageRating: oneOfType([number, string]),
      totalReviews: oneOfType([number, string])
    })
  }).isRequired,
  productPrice: number,
  storeId: string.isRequired,
  schemaName: string,
  index: number,
  currentHotspotIndex: number,
  setCurrentHotspotIndex: func
};

Hotspot.defaultProps = {
  productPrice: null,
  schemaName: null,
  index: 0,
  currentHotspotIndex: null,
  setCurrentHotspotIndex: null
};

Hotspot.dataModel = extend({}, Details);

export { Hotspot };
